import React, { useMemo } from "react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { PALETTE, fmtNumber, fmtDays, daysFromMoney } from "@/lib/chart-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * SectorRadar — Radar "Retour vitaliste sectoriel".
 *
 * Axes fixes : Santé, Éducation, Sécurité, Justice, Infrastructure.
 *
 * Si des `results.sectors.sectors[]` sont fournis : on utilise service_value vs life_invested.
 * Sinon, on matche les budget_lines agrégées sur les labels d'axes (best-effort) pour donner
 * l'investissement vitaliste, avec la restitution estimée à 0 si non renseignée.
 */
const AXES = ["Santé", "Éducation", "Sécurité", "Justice", "Infrastructure"];

const MATCH_KEYWORDS = {
  "Santé": ["santé", "health", "hôpital", "hopitaux"],
  "Éducation": ["éduc", "educ", "school", "enseignement"],
  "Sécurité": ["défense", "defense", "sécurité", "securit", "armée", "police"],
  "Justice": ["justice", "juridict", "pénal", "penal"],
  "Infrastructure": ["infrastructure", "travaux", "routes", "transport"],
};

function matchSector(label) {
  if (!label) return null;
  const low = String(label).toLowerCase();
  for (const axis of AXES) {
    const kws = MATCH_KEYWORDS[axis];
    if (kws.some((kw) => low.includes(kw))) return axis;
  }
  return null;
}

export default function SectorRadar({
  sectorsResult,
  aggregatedBudgetLines = [],
  salaireJournalierMoyen,
}) {
  const data = useMemo(() => {
    // Build a baseline row per axis
    const row = AXES.map((axis) => ({
      axis,
      invested: 0,
      returned: 0,
    }));

    // Prefer explicit sectors from compute_sector_returns if available
    if (sectorsResult?.available && Array.isArray(sectorsResult.sectors)) {
      sectorsResult.sectors.forEach((s) => {
        const a = matchSector(s.sector) || s.sector;
        const target = row.find((r) => r.axis === a);
        if (!target) return;
        target.invested += Number(s.value_life_invested) || 0;
        target.returned += Number(s.value_service) || 0;
      });
    }

    // Otherwise derive invested from aggregated budget lines (in days of life)
    const hasExplicit = sectorsResult?.available;
    if (!hasExplicit) {
      aggregatedBudgetLines.forEach((bl) => {
        const axis = matchSector(bl.sector);
        if (!axis) return;
        const target = row.find((r) => r.axis === axis);
        if (!target) return;
        const days =
          daysFromMoney(Number(bl.amount) || 0, salaireJournalierMoyen) || 0;
        target.invested += days;
        // No explicit return estimate: keep at 0
      });
    }

    return row;
  }, [sectorsResult, aggregatedBudgetLines, salaireJournalierMoyen]);

  const maxVal = Math.max(
    1,
    ...data.map((d) => Math.max(d.invested, d.returned))
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        className="rounded-md border border-border bg-card p-3 text-xs shadow-lg"
        data-testid="tooltip-radar"
      >
        <div className="font-semibold">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="mt-1 flex justify-between gap-4">
            <span className="text-muted-foreground" style={{ color: p.color }}>
              {p.name}
            </span>
            <span className="font-mono">
              {p.name.toLowerCase().includes("invest")
                ? fmtDays(p.value)
                : fmtNumber(p.value, 2)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card data-testid="chart-radar">
      <CardHeader>
        <CardTitle className="text-base">
          Retour vitaliste sectoriel
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-72 w-full" data-testid="chart-radar-container">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={data} outerRadius="72%">
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis
                dataKey="axis"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              />
              <PolarRadiusAxis
                angle={90}
                tick={false}
                axisLine={false}
                domain={[0, maxVal]}
              />
              <Radar
                name="Vie investie (jours)"
                dataKey="invested"
                stroke={PALETTE.blue}
                fill={PALETTE.blue}
                fillOpacity={0.35}
              />
              <Radar
                name="Service restitué"
                dataKey="returned"
                stroke={PALETTE.gold}
                fill={PALETTE.gold}
                fillOpacity={0.35}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        {!sectorsResult?.available && (
          <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
            La restitution sectorielle nécessite des données de valeur de
            service public. Seule la vie investie est calculée à partir des
            lignes budgétaires extraites.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
